import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useEffect } from 'react';
import { useGetFinancialMetricsMutation } from 'services/api/relifDashboard.api';

import useFinancialData from '../hooks/useFinancialData';
import CostsLineChart from './charts/CostsLineChart';
import MonthlyBarChart from './charts/MonthlyBarChart';
import LoadingState from './common/LoadingState';
import StatCard from './StatCard';

interface FinancialPerformanceDashboardProps {
  selectedYear: number;
}

const FinancialPerformanceDashboard: React.FC<FinancialPerformanceDashboardProps> = ({ selectedYear }) => {
  const [getFinancialMetrics, { data: financialData, isLoading }] = useGetFinancialMetricsMutation();

  useEffect(() => {
    getFinancialMetrics({ year: selectedYear }).catch(() => {});
  }, [selectedYear, getFinancialMetrics]);

  const { contributionMarginData, costsByMonthData, totalCosts } = useFinancialData(financialData);

  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        Rendimiento Financiero
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Margen de Contribución Promedio"
            value={financialData?.contributionMargin?.average || 0}
            subtitle="Últimos 6 meses"
            color="primary"
            isPercentage
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Net Revenue Retention (U3M)"
            value={financialData?.netRevenueRetention?.u3M || 0}
            subtitle="Últimos 3 meses"
            color="success"
            isPercentage
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Net Revenue Retention (U12M)"
            value={financialData?.netRevenueRetention?.u12M || 0}
            subtitle="Últimos 12 meses"
            color="info"
            isPercentage
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Costos Totales"
            value={Math.round(totalCosts).toLocaleString('es-CL')}
            subtitle="AI + Floid + Meta (CLP)"
            color="warning"
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Costos AI"
            value={Math.round(financialData?.costs?.ai || 0).toLocaleString('es-CL')}
            subtitle={`Modelos de IA - ${selectedYear} (CLP)`}
            color="secondary"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Costos Floid"
            value={Math.round(financialData?.costs?.floid || 0).toLocaleString('es-CL')}
            subtitle={`Consultas Floid - ${selectedYear} (CLP)`}
            color="secondary"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <StatCard
            title="Costos Meta"
            value={Math.round(financialData?.costs?.meta || 0).toLocaleString('es-CL')}
            subtitle={`Plantillas WhatsApp - ${selectedYear} (CLP)`}
            color="secondary"
          />
        </Grid>
      </Grid>
      {costsByMonthData.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={12}>
            <CostsLineChart title={`Costos por Mes - ${selectedYear} (CLP)`} data={costsByMonthData} yAxisLabel="CLP" />
          </Grid>
        </Grid>
      )}
      {contributionMarginData.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={12}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Margen de Contribución - Últimos 6 Meses (Trailing)
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 2,
                  }}
                >
                  Fórmula: ((Facturación) - (Costo de Meta + Costo Modelos AI + Costo Floid u otro)) / (Facturación)
                </Typography>
                <Box sx={{ width: '100%', overflow: 'hidden' }}>
                  <MonthlyBarChart
                    title="Margen de Contribución (%)"
                    data={contributionMarginData.map((d) => ({ label: d.label, value: d.value }))}
                    yAxisLabel="%"
                    yAxisMin={0}
                    height={350}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      {contributionMarginData.length === 0 && !isLoading && (
        <Card sx={{ borderRadius: 3, p: 3, mt: 4 }}>
          <Typography
            variant="body1"
            align="center"
            sx={{
              color: 'text.secondary',
            }}
          >
            No hay datos de rendimiento financiero disponibles
          </Typography>
        </Card>
      )}
    </Box>
  );
};

export default FinancialPerformanceDashboard;
